/* eslint-disable react/forbid-dom-props, react/forbid-component-props */
import * as React from "react";
import { useState, useEffect } from "react";
import {
  makeStyles,
  tokens,
  Button,
  Caption1,
  Body1,
  Spinner,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Dropdown,
  Option,
  Tab,
  TabList,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Subtitle1,
} from "@fluentui/react-components";
import {
  TableSimple24Regular,
  DataTrending24Regular,
  ArrowTrending24Regular,
  Checkmark24Regular,
  LockClosed20Regular,
  Star24Regular,
  Person24Regular,
  BookOpen24Regular,
  CalendarLtr24Regular,
  Eye24Regular,
  Payment24Regular,
  RibbonStar24Regular,
  Gift24Regular,
  AlertUrgent24Regular,
} from "@fluentui/react-icons";
import { User } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "../../config";
import { Plan, MARKETPLACE_OFFER_URL } from "../../services/subscription";
import {
  getSubscriptionRecord,
  startPro7DayTrial,
  checkTemplateAccess,
  checkCategoryLifetimeUnlock,
  SubscriptionRecord
} from "../../services/saasService";

// ─── Types & Constants ────────────────────────────────────────────────────────

export type IndustryOption =
  | "All"
  | "Strategic Planning"
  | "Project Management"
  | "Academic & Research"
  | "Professional Productivity";

export interface MarketplaceTemplate {
  id: string;
  title: string;
  description: string;
  category: "General" | "Professional";
  industry: IndustryOption;
  section: string;
  price: number; // In cents (e.g. 499 = $4.99)
  packId?: string;
  icon: React.ReactElement;
  usesTable: boolean;
  editorContent: string;
  htmlContent: string;
}

interface MarketplaceProps {
  user: User;
  userPlan: Plan;
  onUseTemplate: (templateId: string, title: string, editorContent: string, wordHtml: string, usesTable: boolean) => void;
  onRefreshPlan: () => void;
}

// ─── HTML Template Strings ────────────────────────────────────────────────────

const TH = `background-color:#f3f2f1;color:#222;padding:10px 14px;border:0.5pt solid #d1d1d1;text-align:left;font-weight:700;font-family:Calibri,sans-serif;font-size:10pt;`;
const TD = `padding:10px 14px;border:0.5pt solid #d1d1d1;vertical-align:top;color:#444;font-family:Calibri,sans-serif;font-size:10pt;`;
const TD_LABEL = `padding:10px 14px;border:0.5pt solid #d1d1d1;vertical-align:top;font-weight:600;color:#222;background-color:#fafafa;font-family:Calibri,sans-serif;font-size:10pt;`;
const TABLE = `border-collapse:collapse;width:100%;font-family:Calibri,sans-serif;font-size:10pt;`;

const SWOT_HTML = `
<p><strong style="font-family:Calibri,sans-serif;font-size:11pt;">SWOT Analysis</strong></p>
<table style="${TABLE}">
  <tr>
    <th style="${TH}width:50%;">▲ Strengths</th>
    <th style="${TH}width:50%;">▼ Weaknesses</th>
  </tr>
  <tr>
    <td style="${TD}height:90px;">• [Internal advantage 1]<br/>• [Internal advantage 2]</td>
    <td style="${TD}height:90px;">• [Internal limitation 1]<br/>• [Internal limitation 2]</td>
  </tr>
  <tr>
    <th style="${TH}">◆ Opportunities</th>
    <th style="${TH}">● Threats</th>
  </tr>
  <tr>
    <td style="${TD}height:90px;">• [Market opportunity 1]<br/>• [Market opportunity 2]</td>
    <td style="${TD}height:90px;">• [External threat 1]<br/>• [External threat 2]</td>
  </tr>
</table>`;

const CURRENT_FUTURE_HTML = `
<p><strong style="font-family:Calibri,sans-serif;font-size:11pt;">Current State vs. Future State — Gap Analysis</strong></p>
<table style="${TABLE}">
  <tr>
    <th style="${TH}width:25%;">Focus Area</th>
    <th style="${TH}width:37.5%;">Current State (As-Is)</th>
    <th style="${TH}width:37.5%;">Future State (To-Be)</th>
  </tr>
  <tr><td style="${TD_LABEL}">Process</td><td style="${TD}">[Describe current process]</td><td style="${TD}">[Describe target process]</td></tr>
  <tr><td style="${TD_LABEL}">Technology</td><td style="${TD}">[Current tools/systems]</td><td style="${TD}">[Target tools/systems]</td></tr>
  <tr><td style="${TD_LABEL}">People</td><td style="${TD}">[Current team/skills]</td><td style="${TD}">[Target capabilities]</td></tr>
  <tr><td style="${TD_LABEL}">Metrics</td><td style="${TD}">[Current KPIs / benchmarks]</td><td style="${TD}">[Target KPIs / benchmarks]</td></tr>
</table>`;

const ADKAR_HTML = `
<p><strong style="font-family:Calibri,sans-serif;font-size:11pt;">ADKAR Change Management Framework</strong></p>
<table style="${TABLE}">
  <tr><td style="width:6px;padding:0;border:none;background-color:#59766C;"></td><td style="${TD}"><span style="font-size:9pt;font-weight:800;color:#59766C;text-transform:uppercase;letter-spacing:1px;">A — Awareness</span><br/><span style="color:#444;">Communicate the business need for change. Stakeholders must understand <em>why</em> the change is happening.</span><br/><span style="color:#999;font-size:9pt;font-style:italic;">Key actions: [Town halls, leadership messaging, FAQ documents]</span></td></tr>
  <tr><td style="width:6px;padding:0;border:none;background-color:#3F6B6B;"></td><td style="${TD}"><span style="font-size:9pt;font-weight:800;color:#3F6B6B;text-transform:uppercase;letter-spacing:1px;">D — Desire</span><br/><span style="color:#444;">Build support and motivation. Each individual must choose to participate in the change.</span><br/><span style="color:#999;font-size:9pt;font-style:italic;">Key actions: [Engage champions, address personal impact, leadership endorsement]</span></td></tr>
  <tr><td style="width:6px;padding:0;border:none;background-color:#2D5252;"></td><td style="${TD}"><span style="font-size:9pt;font-weight:800;color:#2D5252;text-transform:uppercase;letter-spacing:1px;">K — Knowledge</span><br/><span style="color:#444;">Train and coach individuals on new processes, skills, and tools.</span><br/><span style="color:#999;font-size:9pt;font-style:italic;">Key actions: [Training curriculum, workshops, peer coaching]</span></td></tr>
  <tr><td style="width:6px;padding:0;border:none;background-color:#1A3A3A;"></td><td style="${TD}"><span style="font-size:9pt;font-weight:800;color:#1A3A3A;text-transform:uppercase;letter-spacing:1px;">A — Ability</span><br/><span style="color:#444;">Demonstrate ability to apply new skills consistently in the work environment.</span><br/><span style="color:#999;font-size:9pt;font-style:italic;">Key actions: [Supervised practice, pilot programs, go-live support]</span></td></tr>
  <tr><td style="width:6px;padding:0;border:none;background-color:#112525;"></td><td style="${TD}"><span style="font-size:9pt;font-weight:800;color:#112525;text-transform:uppercase;letter-spacing:1px;">R — Reinforcement</span><br/><span style="color:#444;">Sustain the change by anchoring it in culture, process, and accountability.</span><br/><span style="color:#999;font-size:9pt;font-style:italic;">Key actions: [Recognition programs, audits, performance metrics, feedback loops]</span></td></tr>
</table>`;

const VRIO_HTML = `
<p><strong style="font-family:Calibri,sans-serif;font-size:11pt;">VRIO Framework — Competitive Advantage Analysis</strong></p>
<table style="${TABLE}">
  <tr>
    <th style="${TH}width:22%;">Capability / Resource</th>
    <th style="${TH}width:13%;">Valuable?</th>
    <th style="${TH}width:13%;">Rare?</th>
    <th style="${TH}width:16%;">Hard to Imitate?</th>
    <th style="${TH}width:13%;">Organized?</th>
    <th style="${TH}width:23%;">Competitive Implication</th>
  </tr>
  <tr>
    <td style="${TD}">[Resource / Capability 1]</td>
    <td style="${TD}">Yes / No</td><td style="${TD}">Yes / No</td><td style="${TD}">Yes / No</td><td style="${TD}">Yes / No</td>
    <td style="${TD}">[Sustained Competitive Advantage]</td>
  </tr>
  <tr>
    <td style="${TD}">[Resource / Capability 2]</td>
    <td style="${TD}">Yes / No</td><td style="${TD}">Yes / No</td><td style="${TD}">Yes / No</td><td style="${TD}">Yes / No</td>
    <td style="${TD}">[Temporary Competitive Advantage]</td>
  </tr>
</table>`;

const BMC_CELL = `vertical-align:top;padding:10px 12px;border:0.5pt solid #d1d1d1;background-color:#fafafa;font-family:Calibri,sans-serif;font-size:9pt;`;
const BMC_ACCENT = `vertical-align:top;padding:10px 12px;border:0.5pt solid #d1d1d1;background-color:#f0f7f4;font-family:Calibri,sans-serif;font-size:9pt;`;
const BMC_COST = `vertical-align:top;padding:10px 12px;border:0.5pt solid #d1d1d1;background-color:#fff8f0;font-family:Calibri,sans-serif;font-size:9pt;`;
const BMC_REV = `vertical-align:top;padding:10px 12px;border:0.5pt solid #d1d1d1;background-color:#f0f7f4;font-family:Calibri,sans-serif;font-size:9pt;`;

const BMC_HTML = `
<p><strong style="font-family:Calibri,sans-serif;font-size:11pt;">Business Model Canvas</strong></p>
<table style="border-collapse:collapse;width:100%;font-family:Calibri,sans-serif;font-size:9pt;">
  <tr>
    <td rowspan="2" style="${BMC_CELL}width:20%;"><strong style="display:block;margin-bottom:4px;font-size:8pt;text-transform:uppercase;letter-spacing:.5px;color:#555;">KEY PARTNERS</strong>• [Partner 1]<br/>• [Partner 2]</td>
    <td style="${BMC_CELL}width:20%;"><strong style="display:block;margin-bottom:4px;font-size:8pt;text-transform:uppercase;letter-spacing:.5px;color:#555;">KEY ACTIVITIES</strong>• [Activity 1]</td>
    <td rowspan="2" style="${BMC_ACCENT}width:20%;"><strong style="display:block;margin-bottom:4px;font-size:8pt;text-transform:uppercase;letter-spacing:.5px;color:#59766C;">VALUE PROPOSITIONS</strong>• [VP 1]</td>
    <td style="${BMC_CELL}width:20%;"><strong style="display:block;margin-bottom:4px;font-size:8pt;text-transform:uppercase;letter-spacing:.5px;color:#555;">CUSTOMER RELATIONSHIPS</strong>• [Relationship]</td>
    <td rowspan="2" style="${BMC_CELL}width:20%;"><strong style="display:block;margin-bottom:4px;font-size:8pt;text-transform:uppercase;letter-spacing:.5px;color:#555;">CUSTOMER SEGMENTS</strong>• [Segment 1]</td>
  </tr>
  <tr>
    <td style="${BMC_CELL}"><strong style="display:block;margin-bottom:4px;font-size:8pt;text-transform:uppercase;letter-spacing:.5px;color:#555;">KEY RESOURCES</strong>• [Resource]</td>
    <td style="${BMC_CELL}"><strong style="display:block;margin-bottom:4px;font-size:8pt;text-transform:uppercase;letter-spacing:.5px;color:#555;">CHANNELS</strong>• [Channel]</td>
  </tr>
</table>`;

const RAID_HTML = `
<p><strong style="font-family:Calibri,sans-serif;font-size:11pt;">RAID Log — Risks, Actions, Issues &amp; Decisions</strong></p>
<table style="${TABLE}">
  <tr>
    <th style="${TH}width:10%;">ID</th>
    <th style="${TH}width:20%;">Type</th>
    <th style="${TH}width:40%;">Description</th>
    <th style="${TH}width:30%;">Owner</th>
  </tr>
  <tr style="background-color:#fff5f5;"><td style="${TD}">R-001</td><td style="${TD}font-weight:700;color:#c0392b;">▲ Risk</td><td style="${TD}">[Describe risk]</td><td style="${TD}">[Owner]</td></tr>
</table>`;

const CORNELL_HTML = `
<p><strong style="font-family:Calibri,sans-serif;font-size:11pt;">Cornell Notes</strong></p>
<table style="${TABLE}">
  <tr>
    <td style="${TD}width:30%;border-right:2pt solid #59766C;"><strong style="color:#59766C;">Cue Column</strong><br/>[Keywords]</td>
    <td style="${TD}width:70%;"><strong>Notes</strong><br/>• [Details]</td>
  </tr>
</table>`;

const BUDGET_HTML = `
<p><strong style="font-family:Calibri,sans-serif;font-size:11pt;">Monthly Budget</strong></p>
<table style="${TABLE}">
  <tr style="background-color:#e8f5e9;"><th style="${TH}">Income Source</th><th style="${TH}">Budgeted</th><th style="${TH}">Actual</th></tr>
  <tr><td style="${TD}">Salary</td><td style="${TD}">$0.00</td><td style="${TD}">$0.00</td></tr>
</table>`;

const LIT_REVIEW_HTML = `
<p><strong style="font-family:Calibri,sans-serif;font-size:11pt;">Literature Review Matrix</strong></p>
<table style="${TABLE}">
  <tr>
    <th style="${TH}width:18%;">Source (Author, Year)</th>
    <th style="${TH}width:18%;">Research Question</th>
    <th style="${TH}width:14%;">Methodology</th>
    <th style="${TH}width:20%;">Key Findings</th>
    <th style="${TH}width:18%;">Relevance to My Work</th>
    <th style="${TH}width:12%;">Limitations</th>
  </tr>
  <tr>
    <td style="${TD}">[Author, Y. (Year)]</td>
    <td style="${TD}">[Research question / thesis]</td>
    <td style="${TD}">[Qualitative / Quantitative / Mixed]</td>
    <td style="${TD}">[Key findings]</td>
    <td style="${TD}">[How it relates]</td>
    <td style="${TD}">[Study limitations]</td>
  </tr>
  <tr>
    <td style="${TD}">[Author, Y. (Year)]</td>
    <td style="${TD}">[Research question / thesis]</td>
    <td style="${TD}">[Qualitative / Quantitative / Mixed]</td>
    <td style="${TD}">[Key findings]</td>
    <td style="${TD}">[How it relates]</td>
    <td style="${TD}">[Study limitations]</td>
  </tr>
  <tr>
    <td style="${TD}">[Author, Y. (Year)]</td>
    <td style="${TD}">[Research question / thesis]</td>
    <td style="${TD}">[Qualitative / Quantitative / Mixed]</td>
    <td style="${TD}">[Key findings]</td>
    <td style="${TD}">[How it relates]</td>
    <td style="${TD}">[Study limitations]</td>
  </tr>
</table>`;

const DIAMOND_DK = `background-color:#1a2e2a;color:#ffffff;padding:14px 18px;border:0.5pt solid #d1d1d1;font-family:Calibri,sans-serif;font-size:10pt;font-weight:700;text-align:center;`;
const DIAMOND_MID = `background-color:#f8fbfa;color:#222;padding:12px 16px;border:0.5pt solid #d1d1d1;font-family:Calibri,sans-serif;font-size:10pt;vertical-align:top;`;
const DIAMOND_INSIGHT = `background-color:#eaf4f0;color:#1a2e2a;padding:14px 18px;border:0.5pt solid #59766C;font-family:Calibri,sans-serif;font-size:10pt;font-weight:700;text-align:center;border-left:4pt solid #59766C;`;

const DIAMOND_HTML = `
<p><strong style="font-family:Calibri,sans-serif;font-size:11pt;">The Diamond Method — Forum Post Framework</strong></p>
<p style="font-family:Calibri,sans-serif;font-size:9pt;color:#666;margin-top:-2px;margin-bottom:10px;">Frame &nbsp;·&nbsp; Advocate &nbsp;·&nbsp; Illustrate &nbsp;·&nbsp; Inquire &nbsp;|&nbsp; Adapted from Gary (2016); Torbert (2004)</p>
<table style="${TABLE}">

  <!-- Header -->
  <tr>
    <th style="${DIAMOND_DK}width:20%;text-align:center;">Base</th>
    <th style="${DIAMOND_DK}width:80%;text-align:left;">My Post</th>
  </tr>

  <!-- Title -->
  <tr>
    <td style="${TH}text-align:center;vertical-align:middle;">Title</td>
    <td style="${TD}"><strong>[Your forum post title]</strong></td>
  </tr>

  <!-- 1. Frame -->
  <tr>
    <td style="${TH}text-align:center;vertical-align:top;">
      <strong>1</strong><br/>Frame
    </td>
    <td style="${TD}">
      <span style="font-size:8.5pt;color:#555;font-style:italic;display:block;margin-bottom:6px;">State the perspective you are taking and how your post relates to the topic or your colleague's post. Explicitly frame the issue — don't let others guess your angle.</span>
      [Your framing statement here]
    </td>
  </tr>

  <!-- 2. Advocate -->
  <tr>
    <td style="${TH}text-align:center;vertical-align:top;">
      <strong>2</strong><br/>Advocate
    </td>
    <td style="${TD}">
      <span style="font-size:8.5pt;color:#555;font-style:italic;display:block;margin-bottom:6px;">Explicitly assert a position, option, or strategy. Make a point, take a stand, make a claim. Advocate the arguments of the research literature — not personal opinion alone.</span>
      [Your scholarly argument here, citing research literature]
    </td>
  </tr>

  <!-- 3. Illustrate -->
  <tr>
    <td style="${TH}text-align:center;vertical-align:top;">
      <strong>3</strong><br/>Illustrate
    </td>
    <td style="${TD}">
      <span style="font-size:8.5pt;color:#555;font-style:italic;display:block;margin-bottom:6px;">Relate your point to a concrete example from your own experience or vicariously from that of others. Help others see your point in action.</span>
      [Your concrete example or evidence here]
    </td>
  </tr>

  <!-- 4. Inquire -->
  <tr>
    <td style="${TH}text-align:center;vertical-align:top;">
      <strong>4</strong><br/>Inquire
    </td>
    <td style="${TD}">
      <span style="font-size:8.5pt;color:#555;font-style:italic;display:block;margin-bottom:6px;">Close by asking a question of your colleagues. Leave the conversation open — a good post invites others to continue the dialogue.</span>
      [Your open-ended question here]
    </td>
  </tr>

  <!-- References -->
  <tr>
    <td style="${TH}text-align:center;vertical-align:top;">References</td>
    <td style="${TD}">
      <strong>References</strong><br/>
      [Author, A. A. (Year). Title of work. Publisher.]<br/>
      [Author, B. B. (Year). Title of work. Journal, Volume(Issue), pages.]
    </td>
  </tr>

</table>
<p style="font-family:Calibri,sans-serif;font-size:8pt;color:#888;margin-top:8px;">Gary, O. (2016). Forum participation made easy. ORU Online. Adapted from Torbert, W. R. (2004). <em>Action inquiry: The secret of timely and transforming leadership.</em> Berrett-Koehler, pp. 28–29.</p>`;

const ACTION_ITEMS_HTML = `
<p><strong style="font-family:Calibri,sans-serif;font-size:11pt;">Action Items Log</strong></p>
<table style="${TABLE}">
  <tr>
    <th style="${TH}width:5%;">#</th>
    <th style="${TH}width:45%;">Action</th>
    <th style="${TH}width:25%;">Owner</th>
    <th style="${TH}width:25%;">Due Date</th>
  </tr>
  <tr><td style="${TD}">1</td><td style="${TD}">[Action description]</td><td style="${TD}">[Name]</td><td style="${TD}">[Date]</td></tr>
  <tr><td style="${TD}">2</td><td style="${TD}">[Action description]</td><td style="${TD}">[Name]</td><td style="${TD}">[Date]</td></tr>
  <tr><td style="${TD}">3</td><td style="${TD}">[Action description]</td><td style="${TD}">[Name]</td><td style="${TD}">[Date]</td></tr>
</table>`;

const MOSCOW_HTML = `
<p><strong style="font-family:Calibri,sans-serif;font-size:11pt;">MoSCoW Matrix — Requirement Prioritization</strong></p>
<table style="${TABLE}">
  <tr style="background-color:#e8f5e9;">
    <td colspan="4" style="font-weight:700;color:#2e7d32;padding:8px 14px;border:0.5pt solid #d1d1d1;font-family:Calibri,sans-serif;font-size:10pt;">🟢 MUST HAVE — Critical, non-negotiable requirements</td>
  </tr>
  <tr>
    <th style="${TH}width:25%;">Requirement</th>
    <th style="${TH}width:35%;">Description</th>
    <th style="${TH}width:25%;">Rationale</th>
    <th style="${TH}width:15%;">Owner</th>
  </tr>
  <tr><td style="${TD}">[MH-001]</td><td style="${TD}">[Requirement description]</td><td style="${TD}">[Why it is essential]</td><td style="${TD}">[Owner]</td></tr>
  <tr style="background-color:#e3f2fd;">
    <td colspan="4" style="font-weight:700;color:#1565c0;padding:8px 14px;border:0.5pt solid #d1d1d1;font-family:Calibri,sans-serif;font-size:10pt;">🔵 SHOULD HAVE — Important but not critical</td>
  </tr>
  <tr><td style="${TD}">[SH-001]</td><td style="${TD}">[Requirement description]</td><td style="${TD}">[Why it is important]</td><td style="${TD}">[Owner]</td></tr>
  <tr style="background-color:#fffde7;">
    <td colspan="4" style="font-weight:700;color:#f57f17;padding:8px 14px;border:0.5pt solid #d1d1d1;font-family:Calibri,sans-serif;font-size:10pt;">🟡 COULD HAVE — Nice-to-have if time/budget allows</td>
  </tr>
  <tr><td style="${TD}">[CH-001]</td><td style="${TD}">[Requirement description]</td><td style="${TD}">[Why it is desirable]</td><td style="${TD}">[Owner]</td></tr>
  <tr style="background-color:#f5f5f5;">
    <td colspan="4" style="font-weight:700;color:#616161;padding:8px 14px;border:0.5pt solid #d1d1d1;font-family:Calibri,sans-serif;font-size:10pt;">⚪ WON'T HAVE — Out of scope for this iteration</td>
  </tr>
  <tr><td style="${TD}">[WH-001]</td><td style="${TD}">[Requirement description]</td><td style="${TD}">[Why deferred]</td><td style="${TD}">[Owner]</td></tr>
</table>`;

const WEEKLY_PLANNER_HTML = `
<p><strong style="font-family:Calibri,sans-serif;font-size:11pt;">Weekly Planner — Week of: [Date]</strong></p>
<table style="${TABLE}">
  <tr>
    <th style="${TH}width:11%;">Time</th>
    <th style="${TH}width:12.7%;">Mon</th>
    <th style="${TH}width:12.7%;">Tue</th>
    <th style="${TH}width:12.7%;">Wed</th>
    <th style="${TH}width:12.7%;">Thu</th>
    <th style="${TH}width:12.7%;">Fri</th>
    <th style="${TH}width:12.7%;">Sat</th>
    <th style="${TH}width:12.7%;">Sun</th>
  </tr>
  <tr><td style="${TD_LABEL}">Morning</td><td style="${TD}"></td><td style="${TD}"></td><td style="${TD}"></td><td style="${TD}"></td><td style="${TD}"></td><td style="${TD}"></td><td style="${TD}"></td></tr>
  <tr><td style="${TD_LABEL}">Afternoon</td><td style="${TD}"></td><td style="${TD}"></td><td style="${TD}"></td><td style="${TD}"></td><td style="${TD}"></td><td style="${TD}"></td><td style="${TD}"></td></tr>
  <tr><td style="${TD_LABEL}">Evening</td><td style="${TD}"></td><td style="${TD}"></td><td style="${TD}"></td><td style="${TD}"></td><td style="${TD}"></td><td style="${TD}"></td><td style="${TD}"></td></tr>
  <tr><td style="${TD_LABEL}">Top Priority</td><td style="${TD}"></td><td style="${TD}"></td><td style="${TD}"></td><td style="${TD}"></td><td style="${TD}"></td><td style="${TD}"></td><td style="${TD}"></td></tr>
</table>`;

const RECIPE_HTML = `
<p><strong style="font-family:Calibri,sans-serif;font-size:11pt;">Recipe Tracker — [Recipe Name]</strong></p>
<table style="${TABLE}">
  <tr><td style="${TD_LABEL}width:30%;">Prep Time</td><td style="${TD}">[e.g., 15 mins]</td></tr>
  <tr><td style="${TD_LABEL}">Cook Time</td><td style="${TD}">[e.g., 30 mins]</td></tr>
  <tr><td style="${TD_LABEL}">Servings</td><td style="${TD}">[e.g., 4]</td></tr>
  <tr><td style="${TD_LABEL}">Difficulty</td><td style="${TD}">Easy / Medium / Hard</td></tr>
  <tr style="background-color:#e8f5e9;"><td colspan="2" style="font-weight:700;color:#2e7d32;padding:8px 14px;border:0.5pt solid #d1d1d1;font-family:Calibri,sans-serif;font-size:10pt;">🥕 INGREDIENTS</td></tr>
  <tr><th style="${TH}">Ingredient</th><th style="${TH}">Quantity</th></tr>
  <tr><td style="${TD}">[Ingredient 1]</td><td style="${TD}">[Amount &amp; unit]</td></tr>
  <tr><td style="${TD}">[Ingredient 2]</td><td style="${TD}">[Amount &amp; unit]</td></tr>
  <tr><td style="${TD}">[Ingredient 3]</td><td style="${TD}">[Amount &amp; unit]</td></tr>
  <tr style="background-color:#e3f2fd;"><td colspan="2" style="font-weight:700;color:#1565c0;padding:8px 14px;border:0.5pt solid #d1d1d1;font-family:Calibri,sans-serif;font-size:10pt;">📋 INSTRUCTIONS</td></tr>
  <tr><td style="${TD_LABEL}">Step 1</td><td style="${TD}">[Describe step]</td></tr>
  <tr><td style="${TD_LABEL}">Step 2</td><td style="${TD}">[Describe step]</td></tr>
  <tr><td style="${TD_LABEL}">Step 3</td><td style="${TD}">[Describe step]</td></tr>
  <tr style="background-color:#fff8f0;"><td colspan="2" style="font-weight:700;color:#e67e22;padding:8px 14px;border:0.5pt solid #d1d1d1;font-family:Calibri,sans-serif;font-size:10pt;">📊 NUTRITIONAL NOTES</td></tr>
  <tr><td style="${TD_LABEL}">Calories (per serving)</td><td style="${TD}"></td></tr>
  <tr><td style="${TD_LABEL}">Protein</td><td style="${TD}"></td></tr>
  <tr><td style="${TD_LABEL}">Notes / Variations</td><td style="${TD}">[Dietary info, allergies, substitutions]</td></tr>
</table>`;

const CPM_HTML = `
<p><strong style="font-family:Calibri,sans-serif;font-size:11pt;">Critical Path Method (CPM) — Project Schedule Analysis</strong></p>
<table style="${TABLE}">
  <!-- Project Header -->
  <tr>
    <td style="${TD_LABEL}width:25%;">Project Name</td>
    <td style="${TD}width:75%;" colspan="3">[Enter project name]</td>
  </tr>
  <tr>
    <td style="${TD_LABEL}">Project Manager</td>
    <td style="${TD}width:25%;">[Name]</td>
    <td style="${TD_LABEL}width:25%;">Start Date</td>
    <td style="${TD}width:25%;">[MM/DD/YYYY]</td>
  </tr>
  <tr>
    <td style="${TD_LABEL}">Total Project Duration</td>
    <td style="${TD}">[X days / weeks]</td>
    <td style="${TD_LABEL}">Target End Date</td>
    <td style="${TD}">[MM/DD/YYYY]</td>
  </tr>
  <!-- Task Inventory -->
  <tr style="background-color:#e3f2fd;">
    <td colspan="4" style="font-weight:700;color:#1565c0;padding:8px 14px;border:0.5pt solid #d1d1d1;font-family:Calibri,sans-serif;font-size:10pt;">📋 TASK INVENTORY &amp; SCHEDULE ANALYSIS</td>
  </tr>
  <tr>
    <th style="${TH}width:6%;">ID</th>
    <th style="${TH}width:22%;">Task Name</th>
    <th style="${TH}width:8%;text-align:center;">Duration</th>
    <th style="${TH}width:12%;">Depends On</th>
    <th style="${TH}width:8%;text-align:center;">ES</th>
    <th style="${TH}width:8%;text-align:center;">EF</th>
    <th style="${TH}width:8%;text-align:center;">LS</th>
    <th style="${TH}width:8%;text-align:center;">LF</th>
    <th style="${TH}width:8%;text-align:center;">Slack</th>
    <th style="${TH}width:12%;text-align:center;">Critical Path?</th>
  </tr>
  <tr style="background-color:#fff9e6;">
    <td style="${TD}font-weight:700;">A</td>
    <td style="${TD}">[Task A name]</td>
    <td style="${TD}text-align:center;">10</td>
    <td style="${TD}text-align:center;">—</td>
    <td style="${TD}text-align:center;">0</td>
    <td style="${TD}text-align:center;">10</td>
    <td style="${TD}text-align:center;">0</td>
    <td style="${TD}text-align:center;">10</td>
    <td style="${TD}text-align:center;font-weight:700;color:#2e7d32;">0</td>
    <td style="${TD}text-align:center;font-weight:700;color:#c0392b;">✓ YES</td>
  </tr>
  <tr style="background-color:#fff9e6;">
    <td style="${TD}font-weight:700;">B</td>
    <td style="${TD}">[Task B name]</td>
    <td style="${TD}text-align:center;">12</td>
    <td style="${TD}text-align:center;">A</td>
    <td style="${TD}text-align:center;">10</td>
    <td style="${TD}text-align:center;">22</td>
    <td style="${TD}text-align:center;">10</td>
    <td style="${TD}text-align:center;">22</td>
    <td style="${TD}text-align:center;font-weight:700;color:#2e7d32;">0</td>
    <td style="${TD}text-align:center;font-weight:700;color:#c0392b;">✓ YES</td>
  </tr>
  <tr style="background-color:#fff9e6;">
    <td style="${TD}font-weight:700;">C</td>
    <td style="${TD}">[Task C name]</td>
    <td style="${TD}text-align:center;">9</td>
    <td style="${TD}text-align:center;">B</td>
    <td style="${TD}text-align:center;">22</td>
    <td style="${TD}text-align:center;">31</td>
    <td style="${TD}text-align:center;">22</td>
    <td style="${TD}text-align:center;">31</td>
    <td style="${TD}text-align:center;font-weight:700;color:#2e7d32;">0</td>
    <td style="${TD}text-align:center;font-weight:700;color:#c0392b;">✓ YES</td>
  </tr>
  <tr>
    <td style="${TD}font-weight:700;">D</td>
    <td style="${TD}">[Task D name]</td>
    <td style="${TD}text-align:center;">5</td>
    <td style="${TD}text-align:center;">—</td>
    <td style="${TD}text-align:center;">0</td>
    <td style="${TD}text-align:center;">5</td>
    <td style="${TD}text-align:center;">5</td>
    <td style="${TD}text-align:center;">10</td>
    <td style="${TD}text-align:center;color:#888;">5</td>
    <td style="${TD}text-align:center;color:#888;">—</td>
  </tr>
  <tr>
    <td style="${TD}font-weight:700;">E</td>
    <td style="${TD}">[Task E name]</td>
    <td style="${TD}text-align:center;">7</td>
    <td style="${TD}text-align:center;">D</td>
    <td style="${TD}text-align:center;">5</td>
    <td style="${TD}text-align:center;">12</td>
    <td style="${TD}text-align:center;">10</td>
    <td style="${TD}text-align:center;">17</td>
    <td style="${TD}text-align:center;color:#888;">5</td>
    <td style="${TD}text-align:center;color:#888;">—</td>
  </tr>
  <tr>
    <td style="${TD}font-weight:700;">F</td>
    <td style="${TD}">[Task F name]</td>
    <td style="${TD}text-align:center;">6</td>
    <td style="${TD}text-align:center;">E</td>
    <td style="${TD}text-align:center;">12</td>
    <td style="${TD}text-align:center;">18</td>
    <td style="${TD}text-align:center;">17</td>
    <td style="${TD}text-align:center;">23</td>
    <td style="${TD}text-align:center;color:#888;">5</td>
    <td style="${TD}text-align:center;color:#888;">—</td>
  </tr>
  <tr>
    <td style="${TD}font-weight:700;">G</td>
    <td style="${TD}">[Task G name]</td>
    <td style="${TD}text-align:center;">3</td>
    <td style="${TD}text-align:center;">—</td>
    <td style="${TD}text-align:center;">0</td>
    <td style="${TD}text-align:center;">3</td>
    <td style="${TD}text-align:center;">10</td>
    <td style="${TD}text-align:center;">13</td>
    <td style="${TD}text-align:center;color:#888;">10</td>
    <td style="${TD}text-align:center;color:#888;">—</td>
  </tr>
  <tr>
    <td style="${TD}font-weight:700;">H</td>
    <td style="${TD}">[Task H name]</td>
    <td style="${TD}text-align:center;">4</td>
    <td style="${TD}text-align:center;">G</td>
    <td style="${TD}text-align:center;">3</td>
    <td style="${TD}text-align:center;">7</td>
    <td style="${TD}text-align:center;">13</td>
    <td style="${TD}text-align:center;">17</td>
    <td style="${TD}text-align:center;color:#888;">10</td>
    <td style="${TD}text-align:center;color:#888;">—</td>
  </tr>
  <tr>
    <td style="${TD}font-weight:700;">I</td>
    <td style="${TD}">[Task I name]</td>
    <td style="${TD}text-align:center;">6</td>
    <td style="${TD}text-align:center;">H</td>
    <td style="${TD}text-align:center;">7</td>
    <td style="${TD}text-align:center;">13</td>
    <td style="${TD}text-align:center;">17</td>
    <td style="${TD}text-align:center;">23</td>
    <td style="${TD}text-align:center;color:#888;">10</td>
    <td style="${TD}text-align:center;color:#888;">—</td>
  </tr>
  <!-- Column Legend -->
  <tr style="background-color:#fafafa;">
    <td colspan="10" style="padding:6px 14px;border:0.5pt solid #d1d1d1;font-family:Calibri,sans-serif;font-size:9pt;color:#888;font-style:italic;">
      ES = Early Start &nbsp;|&nbsp; EF = Early Finish &nbsp;|&nbsp; LS = Late Start &nbsp;|&nbsp; LF = Late Finish &nbsp;|&nbsp; Slack = LF − EF (0 = critical)
    </td>
  </tr>
  <!-- Path Analysis -->
  <tr style="background-color:#e8f5e9;">
    <td colspan="10" style="font-weight:700;color:#2e7d32;padding:8px 14px;border:0.5pt solid #d1d1d1;font-family:Calibri,sans-serif;font-size:10pt;">🔀 PATH ANALYSIS</td>
  </tr>
  <tr>
    <th style="${TH}width:6%;">Path</th>
    <th style="${TH}width:40%;" colspan="4">Task Sequence</th>
    <th style="${TH}width:20%;text-align:center;" colspan="2">Total Duration</th>
    <th style="${TH}width:34%;text-align:center;" colspan="3">Critical Path?</th>
  </tr>
  <tr style="background-color:#fff9e6;">
    <td style="${TD}font-weight:700;color:#c0392b;">1</td>
    <td style="${TD}" colspan="4">Start → A → B → C → End</td>
    <td style="${TD}font-weight:700;text-align:center;" colspan="2">31 days</td>
    <td style="${TD}font-weight:700;color:#c0392b;text-align:center;" colspan="3">✓ CRITICAL PATH</td>
  </tr>
  <tr>
    <td style="${TD}font-weight:700;">2</td>
    <td style="${TD}" colspan="4">Start → D → E → F → End</td>
    <td style="${TD}text-align:center;" colspan="2">18 days</td>
    <td style="${TD}color:#888;text-align:center;" colspan="3">—</td>
  </tr>
  <tr>
    <td style="${TD}font-weight:700;">3</td>
    <td style="${TD}" colspan="4">Start → G → H → I → End</td>
    <td style="${TD}text-align:center;" colspan="2">13 days</td>
    <td style="${TD}color:#888;text-align:center;" colspan="3">—</td>
  </tr>
  <!-- Critical Path Summary -->
  <tr style="background-color:#fff5f5;">
    <td colspan="10" style="font-weight:700;color:#c0392b;padding:8px 14px;border:0.5pt solid #d1d1d1;font-family:Calibri,sans-serif;font-size:10pt;">🚨 CRITICAL PATH SUMMARY</td>
  </tr>
  <tr>
    <td style="${TD_LABEL}" colspan="2">Critical Path</td>
    <td style="${TD}" colspan="8">Start → A → B → C → End</td>
  </tr>
  <tr>
    <td style="${TD_LABEL}" colspan="2">Total Duration</td>
    <td style="${TD}" colspan="8">31 days — any delay on this path delays the entire project</td>
  </tr>
  <tr>
    <td style="${TD_LABEL}" colspan="2">Key Risk Tasks</td>
    <td style="${TD}" colspan="8">[Identify which critical tasks carry the most uncertainty or dependency risk]</td>
  </tr>
  <tr>
    <td style="${TD_LABEL}" colspan="2">Mitigation Strategy</td>
    <td style="${TD}" colspan="8">[Describe how you will protect these tasks — resource buffers, parallel prep, etc.]</td>
  </tr>
</table>`;

const SOP_HTML = `
<p><strong style="font-family:Calibri,sans-serif;font-size:11pt;">Standard Operating Procedure (SOP)</strong></p>
<table style="${TABLE}">
  <!-- Header block -->
  <tr>
    <td style="${TD_LABEL}width:25%;">SOP Title</td>
    <td style="${TD}width:75%;" colspan="3">[Enter the full name of this procedure]</td>
  </tr>
  <tr>
    <td style="${TD_LABEL}">SOP Number</td>
    <td style="${TD}width:25%;">[e.g., SOP-OPS-001]</td>
    <td style="${TD_LABEL}width:25%;">Version</td>
    <td style="${TD}width:25%;">[e.g., 1.0]</td>
  </tr>
  <tr>
    <td style="${TD_LABEL}">Department / Team</td>
    <td style="${TD}">[Department name]</td>
    <td style="${TD_LABEL}">Effective Date</td>
    <td style="${TD}">[MM/DD/YYYY]</td>
  </tr>
  <tr>
    <td style="${TD_LABEL}">Process Owner</td>
    <td style="${TD}">[Name & Title]</td>
    <td style="${TD_LABEL}">Review Date</td>
    <td style="${TD}">[MM/DD/YYYY]</td>
  </tr>
  <tr>
    <td style="${TD_LABEL}">Approved By</td>
    <td style="${TD}">[Name & Title]</td>
    <td style="${TD_LABEL}">Status</td>
    <td style="${TD}">Draft / Active / Retired</td>
  </tr>
  <!-- Purpose & Scope -->
  <tr style="background-color:#e3f2fd;">
    <td colspan="4" style="font-weight:700;color:#1565c0;padding:8px 14px;border:0.5pt solid #d1d1d1;font-family:Calibri,sans-serif;font-size:10pt;">📋 PURPOSE &amp; SCOPE</td>
  </tr>
  <tr>
    <td style="${TD_LABEL}width:25%;">Purpose</td>
    <td style="${TD}" colspan="3">[Explain why this procedure exists and what outcome it ensures]</td>
  </tr>
  <tr>
    <td style="${TD_LABEL}">Scope</td>
    <td style="${TD}" colspan="3">[Define which teams, systems, or situations this SOP applies to]</td>
  </tr>
  <tr>
    <td style="${TD_LABEL}">Out of Scope</td>
    <td style="${TD}" colspan="3">[List anything explicitly excluded from this procedure]</td>
  </tr>
  <!-- Prerequisites -->
  <tr style="background-color:#fff8f0;">
    <td colspan="4" style="font-weight:700;color:#e67e22;padding:8px 14px;border:0.5pt solid #d1d1d1;font-family:Calibri,sans-serif;font-size:10pt;">⚙️ PREREQUISITES &amp; MATERIALS</td>
  </tr>
  <tr>
    <td style="${TD_LABEL}">Required Access</td>
    <td style="${TD}" colspan="3">[Systems, permissions, or credentials needed]</td>
  </tr>
  <tr>
    <td style="${TD_LABEL}">Tools / Materials</td>
    <td style="${TD}" colspan="3">[Software, equipment, or documents required before starting]</td>
  </tr>
  <!-- Procedure Steps -->
  <tr style="background-color:#e8f5e9;">
    <td colspan="4" style="font-weight:700;color:#2e7d32;padding:8px 14px;border:0.5pt solid #d1d1d1;font-family:Calibri,sans-serif;font-size:10pt;">🔢 PROCEDURE STEPS</td>
  </tr>
  <tr>
    <th style="${TH}width:6%;">Step</th>
    <th style="${TH}width:40%;">Action</th>
    <th style="${TH}width:25%;">Responsible Party</th>
    <th style="${TH}width:29%;">Expected Output / Notes</th>
  </tr>
  <tr>
    <td style="${TD}text-align:center;font-weight:700;color:#2e7d32;">1</td>
    <td style="${TD}">[Describe the first action in clear, active-voice language]</td>
    <td style="${TD}">[Role or Name]</td>
    <td style="${TD}">[What should be produced or verified]</td>
  </tr>
  <tr>
    <td style="${TD}text-align:center;font-weight:700;color:#2e7d32;">2</td>
    <td style="${TD}">[Describe the second action]</td>
    <td style="${TD}">[Role or Name]</td>
    <td style="${TD}">[Expected output or decision point]</td>
  </tr>
  <tr>
    <td style="${TD}text-align:center;font-weight:700;color:#2e7d32;">3</td>
    <td style="${TD}">[Describe the third action]</td>
    <td style="${TD}">[Role or Name]</td>
    <td style="${TD}">[Expected output or decision point]</td>
  </tr>
  <tr>
    <td style="${TD}text-align:center;font-weight:700;color:#2e7d32;">4</td>
    <td style="${TD}">[Describe the fourth action]</td>
    <td style="${TD}">[Role or Name]</td>
    <td style="${TD}">[Expected output or decision point]</td>
  </tr>
  <tr>
    <td style="${TD}text-align:center;font-weight:700;color:#2e7d32;">5</td>
    <td style="${TD}">[Describe the fifth action]</td>
    <td style="${TD}">[Role or Name]</td>
    <td style="${TD}">[Expected output or decision point]</td>
  </tr>
  <!-- Responsibilities (RACI) -->
  <tr style="background-color:#f8f0ff;">
    <td colspan="6" style="font-weight:700;color:#8e44ad;padding:8px 14px;border:0.5pt solid #d1d1d1;font-family:Calibri,sans-serif;font-size:10pt;">👥 RESPONSIBILITIES (RACI)</td>
  </tr>
  <tr>
    <th style="${TH}width:28%;">Role</th>
    <th style="${TH}width:10%;text-align:center;" title="Responsible — does the work">R</th>
    <th style="${TH}width:10%;text-align:center;" title="Accountable — owns the outcome">A</th>
    <th style="${TH}width:10%;text-align:center;" title="Consulted — provides input">C</th>
    <th style="${TH}width:10%;text-align:center;" title="Informed — kept in the loop">I</th>
    <th style="${TH}width:32%;">Notes</th>
  </tr>
  <tr>
    <td style="${TD}">[Process Owner]</td>
    <td style="${TD}text-align:center;">✓</td>
    <td style="${TD}text-align:center;">✓</td>
    <td style="${TD}text-align:center;"></td>
    <td style="${TD}text-align:center;"></td>
    <td style="${TD}">[Maintains and approves the SOP]</td>
  </tr>
  <tr>
    <td style="${TD}">[Team Member / Role]</td>
    <td style="${TD}text-align:center;">✓</td>
    <td style="${TD}text-align:center;"></td>
    <td style="${TD}text-align:center;"></td>
    <td style="${TD}text-align:center;"></td>
    <td style="${TD}">[Executes the procedure steps]</td>
  </tr>
  <tr>
    <td style="${TD}">[Stakeholder / Manager]</td>
    <td style="${TD}text-align:center;"></td>
    <td style="${TD}text-align:center;"></td>
    <td style="${TD}text-align:center;">✓</td>
    <td style="${TD}text-align:center;">✓</td>
    <td style="${TD}">[Reviewed for input; notified on completion]</td>
  </tr>
  <!-- Revision History -->
  <tr style="background-color:#f3f2f1;">
    <td colspan="4" style="font-weight:700;color:#555;padding:8px 14px;border:0.5pt solid #d1d1d1;font-family:Calibri,sans-serif;font-size:10pt;">📝 REVISION HISTORY</td>
  </tr>
  <tr>
    <th style="${TH}width:15%;">Version</th>
    <th style="${TH}width:20%;">Date</th>
    <th style="${TH}width:25%;">Author</th>
    <th style="${TH}width:40%;">Summary of Changes</th>
  </tr>
  <tr>
    <td style="${TD}">1.0</td>
    <td style="${TD}">[MM/DD/YYYY]</td>
    <td style="${TD}">[Author Name]</td>
    <td style="${TD}">Initial version</td>
  </tr>
</table>`;

export const TEMPLATE_CATALOG: MarketplaceTemplate[] = [
  // ── Strategic Planning ─────────────────────────────────────────────────────
  {
    id: "tpl-pro-swot",
    title: "SWOT Analysis",
    description: "Minimalist 2×2 grid assessing Strengths, Weaknesses, Opportunities, and Threats.",
    category: "Professional",
    industry: "Strategic Planning",
    section: "Strategy Frameworks",
    price: 499,
    packId: "pack-strategic",
    icon: <TableSimple24Regular />,
    usesTable: true,
    editorContent: "",
    htmlContent: SWOT_HTML,
  },
  {
    id: "tpl-pro-current-future",
    title: "Current State vs. Future State",
    description: "3-column As-Is / To-Be gap analysis across Process, Technology, People, and Metrics.",
    category: "Professional",
    industry: "Strategic Planning",
    section: "Strategy Frameworks",
    price: 499,
    packId: "pack-strategic",
    icon: <DataTrending24Regular />,
    usesTable: true,
    editorContent: "",
    htmlContent: CURRENT_FUTURE_HTML,
  },
  {
    id: "tpl-pro-vrio",
    title: "VRIO Framework",
    description: "Assess capabilities across Value, Rarity, Imitability, and Organization to identify competitive advantage.",
    category: "Professional",
    industry: "Strategic Planning",
    section: "Strategy Frameworks",
    price: 499,
    packId: "pack-strategic",
    icon: <DataTrending24Regular />,
    usesTable: true,
    editorContent: "",
    htmlContent: VRIO_HTML,
  },
  {
    id: "tpl-pro-bmc",
    title: "Business Model Canvas",
    description: "The classic 9-block Osterwalder canvas for venture and product planning.",
    category: "Professional",
    industry: "Strategic Planning",
    section: "Strategy Frameworks",
    price: 499,
    packId: "pack-strategic",
    icon: <ArrowTrending24Regular />,
    usesTable: true,
    editorContent: "",
    htmlContent: BMC_HTML,
  },
  // ── Project Management ─────────────────────────────────────────────────────
  {
    id: "tpl-general-action",
    title: "Action Items Log",
    description: "Numbered list with Action, Owner, and Due Date columns.",
    category: "Professional",
    industry: "Project Management",
    section: "Planning",
    price: 499,
    packId: "pack-project",
    icon: <Checkmark24Regular />,
    usesTable: true,
    editorContent: "",
    htmlContent: ACTION_ITEMS_HTML,
  },
  {
    id: "tpl-pro-adkar",
    title: "ADKAR Change Management",
    description: "Structured vertical framework for all 5 ADKAR change management elements.",
    category: "Professional",
    industry: "Project Management",
    section: "Change Management",
    price: 499,
    packId: "pack-project",
    icon: <ArrowTrending24Regular />,
    usesTable: true,
    editorContent: "",
    htmlContent: ADKAR_HTML,
  },
  {
    id: "tpl-pro-raid",
    title: "RAID Log",
    description: "Color-coded log for Risks, Actions, Issues, and Decisions across a project.",
    category: "Professional",
    industry: "Project Management",
    section: "Planning",
    price: 499,
    packId: "pack-project",
    icon: <TableSimple24Regular />,
    usesTable: true,
    editorContent: "",
    htmlContent: RAID_HTML,
  },
  {
    id: "tpl-pro-moscow",
    title: "MoSCoW Matrix",
    description: "Priority framework with Must Have, Should Have, Could Have, and Won't Have sections.",
    category: "Professional",
    industry: "Project Management",
    section: "Planning",
    price: 499,
    packId: "pack-project",
    icon: <DataTrending24Regular />,
    usesTable: true,
    editorContent: "",
    htmlContent: MOSCOW_HTML,
  },
  {
    id: "tpl-pro-sop",
    title: "Standard Operating Procedure",
    description: "Full SOP template with Purpose, Scope, Prerequisites, color-coded step table, RACI responsibilities, and Revision History.",
    category: "Professional",
    industry: "Project Management",
    section: "Planning",
    price: 499,
    packId: "pack-project",
    icon: <TableSimple24Regular />,
    usesTable: true,
    editorContent: "",
    htmlContent: SOP_HTML,
  },
  {
    id: "tpl-pro-cpm",
    title: "Critical Path Method (CPM)",
    description: "Full CPM schedule with task inventory (ES, EF, LS, LF, Slack), path analysis across all routes, and a critical path summary with risk and mitigation fields.",
    category: "Professional",
    industry: "Project Management",
    section: "Planning",
    price: 499,
    packId: "pack-project",
    icon: <DataTrending24Regular />,
    usesTable: true,
    editorContent: "",
    htmlContent: CPM_HTML,
  },
  // ── Academic & Research ────────────────────────────────────────────────────
  {
    id: "tpl-pro-cornell",
    title: "Cornell Notes",
    description: "Professional 2-column layout with a Cue column, Notes column, and Summary footer.",
    category: "Professional",
    industry: "Academic & Research",
    section: "Note-Taking",
    price: 499,
    packId: "pack-academic",
    icon: <BookOpen24Regular />,
    usesTable: true,
    editorContent: "",
    htmlContent: CORNELL_HTML,
  },
  {
    id: "tpl-pro-litreview",
    title: "Literature Review Matrix",
    description: "Multi-column table for comparing sources across Research Question, Methodology, Findings, and Relevance.",
    category: "Professional",
    industry: "Academic & Research",
    section: "Research",
    price: 499,
    packId: "pack-academic",
    icon: <BookOpen24Regular />,
    usesTable: true,
    editorContent: "",
    htmlContent: LIT_REVIEW_HTML,
  },
  {
    id: "tpl-pro-diamond",
    title: "The Diamond Method",
    description: "A four-part framework for well-crafted academic forum posts: Frame your perspective, Advocate with research, Illustrate with examples, and Inquire to extend the conversation.",
    category: "Professional",
    industry: "Academic & Research",
    section: "Executive Communication",
    price: 499,
    packId: "pack-academic",
    icon: <Star24Regular />,
    usesTable: true,
    editorContent: "",
    htmlContent: DIAMOND_HTML,
  },
  // ── Professional Productivity ──────────────────────────────────────────────
  {
    id: "tpl-personal-weekly",
    title: "Weekly Planner",
    description: "Minimalist 7-day schedule grid with Morning, Afternoon, Evening, and Priority rows.",
    category: "General",
    industry: "Professional Productivity",
    section: "Planners",
    price: 499,
    packId: "pack-productivity",
    icon: <CalendarLtr24Regular />,
    usesTable: true,
    editorContent: "",
    htmlContent: WEEKLY_PLANNER_HTML,
  },
  {
    id: "tpl-personal-budget",
    title: "Personal Budget",
    description: "Monthly budget tracker with Income, Expenses, Savings Goals, and Net Balance.",
    category: "Professional",
    industry: "Professional Productivity",
    section: "Planners",
    price: 499,
    packId: "pack-productivity",
    icon: <Person24Regular />,
    usesTable: true,
    editorContent: "",
    htmlContent: BUDGET_HTML,
  },
  {
    id: "tpl-personal-recipe",
    title: "Recipe Tracker",
    description: "Formatted layout with sections for Ingredients, Instructions, and Nutritional Notes.",
    category: "Professional",
    industry: "Professional Productivity",
    section: "Planners",
    price: 499,
    packId: "pack-productivity",
    icon: <Star24Regular />,
    usesTable: true,
    editorContent: "",
    htmlContent: RECIPE_HTML,
  },
];

export interface CategoryPack {
  id: string;
  title: string;
  description: string;
  price: number; // 2499 cents = $24.99
  category: string; // matches template.industry
  templatesCount: number;
}

const CATEGORY_PACKS: CategoryPack[] = [
  {
    id: "pack-strategic",
    title: "Strategic Planning Suite",
    description: "SWOT Analysis, Current vs. Future State, VRIO Framework, and Business Model Canvas — plus all future Strategic Planning templates added to this pack.",
    price: 2499,
    category: "Strategic Planning",
    templatesCount: 4   // SWOT, Current vs Future, VRIO, BMC
  },
  {
    id: "pack-project",
    title: "Project Delivery Pack",
    description: "Action Items Log, ADKAR Change Management, RAID Log, MoSCoW Matrix, Standard Operating Procedure, and Critical Path Method — plus all future Project Management templates added to this pack.",
    price: 2499,
    category: "Project Management",
    templatesCount: 6   // Action Items, ADKAR, RAID, MoSCoW, SOP, CPM
  },
  {
    id: "pack-academic",
    title: "Academic & Research Toolkit",
    description: "Cornell Notes, Literature Review Matrix, and The Diamond Method framework — plus all future Academic & Research templates added to this pack.",
    price: 2499,
    category: "Academic & Research",
    templatesCount: 3   // Cornell, Lit Review, Diamond
  },
  {
    id: "pack-productivity",
    title: "Personal Productivity Set",
    description: "Weekly Planner, Personal Budget tracker, and Recipe Tracker — plus all future Professional Productivity templates added to this pack.",
    price: 2499,
    category: "Professional Productivity",
    templatesCount: 3   // Weekly, Budget, Recipe
  }
];

// ─── Styles ───────────────────────────────────────────────────────────────────

const useStyles = makeStyles({
  root: { 
    display: "flex", 
    flexDirection: "column", 
    height: "100%", 
    gap: "12px",
    boxSizing: "border-box"
  },
  tabScroll: {
    overflowY: "auto",
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    paddingBottom: "24px"
  },
  sectionHeader: {
    fontSize: "10px", 
    fontWeight: "700", 
    letterSpacing: "0.12em", 
    textTransform: "uppercase",
    color: tokens.colorNeutralForeground3, 
    padding: "10px 0 6px 0",
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`, 
    marginBottom: "8px",
  },
  card: {
    display: "flex", 
    flexDirection: "column", 
    gap: "8px", 
    padding: "14px",
    borderRadius: "12px", 
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1, 
    boxShadow: "0 2px 4px rgba(0,0,0,0.04)",
    transition: "box-shadow 0.15s ease, border-color 0.15s ease",
    ":hover": { 
      boxShadow: "0 4px 12px rgba(0,0,0,0.08)", 
      borderColor: tokens.colorNeutralStroke1
    },
  },
  cardHeader: {
    display: "flex", 
    alignItems: "center", 
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: "6px"
  },
  cardTitle: {
    fontWeight: 600, 
    fontSize: "14px", 
    color: tokens.colorNeutralForeground1 
  },
  priceBadge: {
    fontSize: "9px", 
    fontWeight: "700", 
    letterSpacing: "0.06em", 
    textTransform: "uppercase",
    borderRadius: "20px", 
    padding: "2px 8px",
    border: `1.5px solid ${tokens.colorNeutralStroke1}`
  },
  btnPrimary: {
    backgroundColor: "#59766C", 
    color: "#fff",
    ":hover": { backgroundColor: "#3F564F" },
  },
  btnOutline: {
    borderColor: "#59766C",
    color: "#59766C",
    ":hover": { backgroundColor: "#f0faf7" }
  },
  tabsList: {
    marginBottom: "4px"
  }
});

// ─── Marketplace Component ────────────────────────────────────────────────────

const Marketplace: React.FC<MarketplaceProps> = ({
  user,
  userPlan,
  onUseTemplate,
  onRefreshPlan
}) => {
  const styles = useStyles();
  const [activeTab, setActiveTab] = useState<"subscription" | "single" | "packs">("single");
  const [subRecord, setSubRecord] = useState<SubscriptionRecord | null>(null);
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annual'>('monthly');
  const [loading, setLoading] = useState(true);
  const [unlockedState, setUnlockedState] = useState<Record<string, boolean>>({});
  const [selectedIndustry, setSelectedIndustry] = useState<IndustryOption>("All");
  const [previewTemplate, setPreviewTemplate] = useState<MarketplaceTemplate | null>(null);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [buyingTemplate, setBuyingTemplate] = useState<MarketplaceTemplate | null>(null);
  const [buyingPack, setBuyingPack] = useState<CategoryPack | null>(null);
  const [stripeBusy, setStripeBusy] = useState(false);

  // Load subscriptions and template accesses
  const loadData = async () => {
    setLoading(true);
    try {
      const sub = await getSubscriptionRecord(user.uid);
      setSubRecord(sub);

      // Check access for each template
      const access: Record<string, boolean> = {};
      for (const t of TEMPLATE_CATALOG) {
        const check = await checkTemplateAccess(user.uid, t.id, t.industry);
        access[t.id] = check.unlocked;
      }
      setUnlockedState(access);
    } catch (err) {
      console.error("Failed to load saas billing records:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [user.uid, userPlan]);

  // ─── SaaS Operations ────────────────────────────────────────────────────────

  const handleStartTrial = async (plan: 'Personal' | 'Professional' = 'Professional') => {
    setLoading(true);
    try {
      await startPro7DayTrial(user.uid, plan);
      setMessage({ type: "success", text: `Subscription successfully activated! Your 7-day trial of ${plan} is now active.` });
      onRefreshPlan();
      await loadData();
    } catch (err) {
      setMessage({ type: "error", text: "Failed to configure subscription." });
    } finally {
      setLoading(false);
    }
  };

  // Opens a Stripe one-time checkout for a template or pack via our own-domain bounce
  // page (keeps the Office dialog origin trusted → no manifest change). The unlock is
  // written by the webhook after payment; we then poll until it appears.
  const startTemplateCheckout = async (
    purchaseType: "single_template" | "category_pack",
    itemId: string,
    title: string,
    checkUnlocked: () => Promise<boolean>
  ) => {
    if (stripeBusy) return;
    setStripeBusy(true);
    setMessage(null);
    try {
      const fns = getFunctions(db.app, "us-central1");
      const call = httpsCallable(fns, "createStripeTemplateCheckout");
      const res: any = await call({ purchaseType, itemId, title, returnOrigin: window.location.origin });
      const url = res?.data?.url;
      if (!url) throw new Error("No checkout URL returned.");

      const goUrl = new URL("stripe-go.html", window.location.href);
      goUrl.searchParams.set("to", url);
      Office.context.ui.displayDialogAsync(
        goUrl.toString(),
        { height: 75, width: 55, displayInIframe: false },
        (asyncResult) => {
          setStripeBusy(false);
          setBuyingTemplate(null);
          setBuyingPack(null);
          if (asyncResult.status !== Office.AsyncResultStatus.Succeeded) {
            setMessage({ type: "error", text: "Could not open checkout. Please try again." });
            return;
          }
          const dialog = (asyncResult as any).value;
          dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg: any) => {
            try {
              const data = JSON.parse(arg.message);
              if (data.type === "STRIPE_DONE" || data.type === "STRIPE_CANCEL") {
                dialog.close();
                if (data.type === "STRIPE_DONE") confirmPurchaseUnlock(title, checkUnlocked);
              }
            } catch (e) { /* ignore non-JSON */ }
          });
          // Fallback: refresh when the dialog is closed manually.
          dialog.addEventHandler(Office.EventType.DialogEventReceived, () => confirmPurchaseUnlock(title, checkUnlocked));
        }
      );
    } catch (err: any) {
      setStripeBusy(false);
      setMessage({ type: "error", text: err?.message || "Could not start checkout. Please try again." });
    }
  };

  // Webhook writes the unlock asynchronously → poll a few times before giving up.
  const confirmPurchaseUnlock = async (title: string, checkUnlocked: () => Promise<boolean>) => {
    for (let i = 0; i < 6; i++) {
      try {
        if (await checkUnlocked()) {
          await loadData();
          setMessage({ type: "success", text: `Unlocked: ${title}` });
          return;
        }
      } catch (e) { /* ignore + retry */ }
      await new Promise((r) => setTimeout(r, 2000));
    }
    // Fallback reload (e.g. dialog closed before payment completed).
    await loadData();
  };

  const handlePurchaseSingle = (template: MarketplaceTemplate) => {
    startTemplateCheckout(
      "single_template",
      template.id,
      template.title,
      () => checkTemplateAccess(user.uid, template.id, template.industry).then((r) => r.unlocked)
    );
  };

  const handlePurchasePack = (pack: CategoryPack) => {
    startTemplateCheckout(
      "category_pack",
      pack.category,
      pack.title,
      () => checkCategoryLifetimeUnlock(user.uid, pack.category)
    );
  };

  // ─── Filtering & Layout Helper ──────────────────────────────────────────────

  const filteredTemplates = TEMPLATE_CATALOG.filter(
    (t) => selectedIndustry === "All" || t.industry === selectedIndustry
  );

  const grouped = filteredTemplates.reduce<Record<string, MarketplaceTemplate[]>>((acc, t) => {
    const key = `${t.industry} › ${t.section}`;
    if (!acc[key]) acc[key] = [];
    acc[key].push(t);
    return acc;
  }, {});

  const countFor = (ind: IndustryOption) =>
    ind === "All" ? TEMPLATE_CATALOG.length : TEMPLATE_CATALOG.filter((t) => t.industry === ind).length;

  if (loading && !subRecord) {
    return <Spinner label="Loading marketplace records..." style={{ marginTop: "40px" }} />;
  }

  return (
    <div className={styles.root}>
      {/* ── Tabs List ── */}
      <TabList
        selectedValue={activeTab}
        onTabSelect={(_, data) => {
          setActiveTab(data.value as any);
          setMessage(null);
        }}
        className={styles.tabsList}
      >
        <Tab value="subscription" icon={<RibbonStar24Regular />}>Subscription</Tab>
        <Tab value="single" icon={<TableSimple24Regular />}>Templates</Tab>
        <Tab value="packs" icon={<Gift24Regular />}>Category Packs</Tab>
      </TabList>

      {/* ── Feedback Message ── */}
      {message && (
        <MessageBar intent={message.type} onDismiss={() => setMessage(null)}>
          <MessageBarBody>{message.text}</MessageBarBody>
        </MessageBar>
      )}

      <div className={styles.tabScroll}>
        {/* ────────────────────────────────────────────────────────────────────── */}
        {/* TAB 1: SUBSCRIPTION                                                    */}
        {/* ────────────────────────────────────────────────────────────────────── */}
        {activeTab === "subscription" && (
          <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
            <div style={{
              background: "linear-gradient(135deg, #1f2e26, #141c18)",
              color: "#fff",
              padding: "16px 20px",
              borderRadius: "16px",
              border: "1px solid rgba(123, 191, 174, 0.2)",
              boxShadow: "0 4px 15px rgba(0, 0, 0, 0.3)"
            }}>
              <Subtitle1 style={{ color: "#7BBFAE", fontWeight: 700, textTransform: "uppercase", fontSize: "11px", letterSpacing: "1px", display: "block" }}>
                Current Workspace Plan
              </Subtitle1>
              <h2 style={{ margin: "4px 0 8px 0", fontSize: "24px", fontWeight: 700, color: "#fff" }}>
                {subRecord?.plan === "Free" || !subRecord?.plan 
                  ? "No Active Subscription" 
                  : subRecord.plan?.toLowerCase() === "enterprise" 
                    ? "Enterprise Plan" 
                    : `${subRecord.plan} Plan`} {subRecord?.status === "trial" ? "(7-Day Free Trial)" : ""}
              </h2>
              <Body1 block style={{ color: "#E0ECE9", fontSize: "13px" }}>
                {subRecord?.plan === "Free" || !subRecord?.plan 
                  ? "🔒 Note creation and premium templates are locked. Start a 7-day free trial of any plan to unlock fully!"
                  : `⭐ Microsoft SaaS Subscription active. Thank you for using MarginPro ${subRecord.plan?.toLowerCase() === "enterprise" ? "Enterprise" : subRecord.plan}!`}
              </Body1>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "16px", marginTop: "10px" }}>
              {/* Billing period toggle */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Subtitle1 style={{ fontWeight: 700, color: tokens.colorNeutralForeground2, fontSize: "14px" }}>
                  SaaS Transactable Plans
                </Subtitle1>
                <div style={{ display: "flex", alignItems: "center", gap: "4px", background: "rgba(0,0,0,0.06)", borderRadius: "20px", padding: "3px" }}>
                  <button
                    onClick={() => setBillingPeriod('monthly')}
                    style={{
                      padding: "4px 12px", borderRadius: "16px", border: "none", cursor: "pointer",
                      fontSize: "11px", fontWeight: 600,
                      background: billingPeriod === 'monthly' ? "#2f8570" : "transparent",
                      color: billingPeriod === 'monthly' ? "#fff" : tokens.colorNeutralForeground3,
                      transition: "all 0.2s"
                    }}
                  >Monthly</button>
                  <button
                    onClick={() => setBillingPeriod('annual')}
                    style={{
                      padding: "4px 12px", borderRadius: "16px", border: "none", cursor: "pointer",
                      fontSize: "11px", fontWeight: 600,
                      background: billingPeriod === 'annual' ? "#2f8570" : "transparent",
                      color: billingPeriod === 'annual' ? "#fff" : tokens.colorNeutralForeground3,
                      transition: "all 0.2s"
                    }}
                  >Annual</button>
                </div>
              </div>
              {billingPeriod === 'annual' && (
                <Caption1 style={{ color: "#2f8570", fontWeight: 600, marginTop: "-8px" }}>
                  🎉 2 months free with annual billing
                </Caption1>
              )}

              {/* Personal Plan Card */}
              <div style={{ 
                padding: "16px", 
                border: subRecord?.plan === "Personal" ? "2px solid #7BBFAE" : `1px solid ${tokens.colorNeutralStroke2}`, 
                borderRadius: "12px", 
                backgroundColor: tokens.colorNeutralBackground2,
                boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                transition: "all 0.2s ease"
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                  <Body1 block style={{ fontWeight: 700, fontSize: "15px", color: tokens.colorNeutralForeground1 }}>
                    Personal Plan
                  </Body1>
                  <div style={{ textAlign: "right" }}>
                    <span style={{ fontSize: "11px", fontWeight: 700, padding: "2px 8px", borderRadius: "10px", background: "rgba(123, 191, 174, 0.1)", color: "#7BBFAE" }}>
                      {billingPeriod === 'annual' ? '$4.99 / mo' : '$5.99 / mo'}
                    </span>
                    {billingPeriod === 'annual' && (
                      <div style={{ fontSize: "9px", color: "#7BBFAE", marginTop: "2px" }}>$59.90 billed annually</div>
                    )}
                  </div>
                </div>
                <Caption1 block style={{ color: tokens.colorNeutralForeground3, marginBottom: "12px", lineHeight: "1.4" }}>
                  Ideal for solo researchers, attorneys, and financial analysts who want full note-taking but prefer individual template purchase options.
                </Caption1>
                <div style={{ fontSize: "11px", color: tokens.colorNeutralForeground2, marginBottom: "16px", display: "flex", flexDirection: "column", gap: "6px" }}>
                  <div>• <strong>7-Day Free Trial</strong> (linked card upfront)</div>
                  <div>• Unlimited notes, secure OneDrive/Vault syncing</div>
                  <div>• Supports secure file and spreadsheet attachments</div>
                  <div>• 🔒 Pre-made premium templates locked (purchased single-use)</div>
                </div>
                {subRecord?.plan === "Personal" ? (
                  <Button
                    appearance="outline"
                    size="small"
                    style={{ width: "100%" }}
                    onClick={() => window.open(MARKETPLACE_OFFER_URL, "_blank")}
                  >
                    Manage Subscription
                  </Button>
                ) : subRecord?.plan === "Free" || !subRecord?.plan ? (
                  <Button
                    appearance="primary"
                    size="small"
                    style={{ width: "100%", backgroundColor: "#7BBFAE", color: "#1A2522" }}
                    onClick={() => handleStartTrial("Personal")}
                  >
                    Start 7-Day Trial
                  </Button>
                ) : (
                  <Button
                    appearance="outline"
                    size="small"
                    style={{ width: "100%" }}
                    onClick={() => window.open(MARKETPLACE_OFFER_URL, "_blank")}
                  >
                    Change to Personal Plan
                  </Button>
                )}
              </div>

              {/* Professional Plan Card */}
              <div style={{ 
                padding: "16px", 
                border: subRecord?.plan === "Professional" ? "2px solid #7BBFAE" : "2px solid rgba(123, 191, 174, 0.4)", 
                borderRadius: "12px", 
                backgroundColor: "rgba(123, 191, 174, 0.05)",
                boxShadow: "0 4px 15px rgba(123, 191, 174, 0.1)",
                position: "relative"
              }}>
                <div style={{ 
                  position: "absolute", 
                  top: "-10px", 
                  right: "12px", 
                  backgroundColor: "#7BBFAE", 
                  color: "#1A2522", 
                  fontSize: "9px", 
                  fontWeight: 800, 
                  padding: "2px 8px", 
                  borderRadius: "10px", 
                  textTransform: "uppercase" 
                }}>
                  Best Value
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                  <Body1 block style={{ fontWeight: 700, fontSize: "15px", color: tokens.colorNeutralForeground1 }}>
                    Professional Plan
                  </Body1>
                  <div style={{ textAlign: "right" }}>
                    <span style={{ fontSize: "11px", fontWeight: 700, padding: "2px 8px", borderRadius: "10px", background: "#7BBFAE", color: "#1A2522" }}>
                      {billingPeriod === 'annual' ? '$8.33 / mo' : '$9.99 / mo'}
                    </span>
                    {billingPeriod === 'annual' && (
                      <div style={{ fontSize: "9px", color: "#7BBFAE", marginTop: "2px" }}>$99.90 billed annually</div>
                    )}
                  </div>
                </div>
                <Caption1 block style={{ color: tokens.colorNeutralForeground3, marginBottom: "12px", lineHeight: "1.4" }}>
                  Includes everything in Personal plus <strong>unlimited instant access to all premium document blueprint templates</strong>.
                </Caption1>
                <div style={{ fontSize: "11px", color: tokens.colorNeutralForeground2, marginBottom: "16px", display: "flex", flexDirection: "column", gap: "6px" }}>
                  <div>• <strong>7-Day Free Trial</strong> (linked card upfront)</div>
                  <div>• ⚡ <strong>All pre-made Marketplace templates 100% unlocked</strong></div>
                  <div>• Unlimited core notes, attachments, and cross-doc sync</div>
                  <div>• Professional, SWOT, project, & legal blueprints</div>
                </div>
                {subRecord?.plan === "Professional" ? (
                  <Button
                    appearance="outline"
                    size="small"
                    style={{ width: "100%" }}
                    onClick={() => window.open(MARKETPLACE_OFFER_URL, "_blank")}
                  >
                    Manage Subscription
                  </Button>
                ) : subRecord?.plan === "Free" || !subRecord?.plan ? (
                  <Button
                    appearance="primary"
                    size="small"
                    style={{ width: "100%", backgroundColor: "#7BBFAE", color: "#1A2522" }}
                    onClick={() => handleStartTrial("Professional")}
                  >
                    Start 7-Day Trial
                  </Button>
                ) : subRecord?.plan === "Personal" ? (
                  <Button
                    appearance="primary"
                    size="small"
                    style={{ width: "100%", backgroundColor: "#7BBFAE", color: "#1A2522" }}
                    onClick={() => window.open(MARKETPLACE_OFFER_URL, "_blank")}
                  >
                    Upgrade to Professional
                  </Button>
                ) : (
                  <Button
                    appearance="outline"
                    size="small"
                    style={{ width: "100%" }}
                    onClick={() => window.open(MARKETPLACE_OFFER_URL, "_blank")}
                  >
                    Change to Professional Plan
                  </Button>
                )}
              </div>

              {/* Enterprise Plan Card */}
              <div style={{ 
                padding: "16px", 
                border: subRecord?.plan?.toLowerCase() === "enterprise" ? "2px solid #7BBFAE" : `1px solid ${tokens.colorNeutralStroke2}`, 
                borderRadius: "12px", 
                backgroundColor: tokens.colorNeutralBackground2,
                boxShadow: "0 2px 10px rgba(0,0,0,0.1)"
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                  <Body1 block style={{ fontWeight: 700, fontSize: "15px", color: tokens.colorNeutralForeground1 }}>
                    Enterprise Plan
                  </Body1>
                  <span style={{ fontSize: "11px", fontWeight: 700, padding: "2px 8px", borderRadius: "10px", background: "rgba(123, 191, 174, 0.1)", color: "#7BBFAE" }}>
                    Custom
                  </span>
                </div>
                <Caption1 block style={{ color: tokens.colorNeutralForeground3, marginBottom: "12px", lineHeight: "1.4" }}>
                  For organizations and departments requiring secure domain Whitelisting, SLA governance, and centralized management.
                </Caption1>
                <div style={{ fontSize: "11px", color: tokens.colorNeutralForeground2, marginBottom: "16px", display: "flex", flexDirection: "column", gap: "6px" }}>
                  <div>• Includes <strong>everything in Professional Plan</strong></div>
                  <div>• Tenant-level single sign-on (SSO) governance</div>
                  <div>• Domain directory whitelisting and auto-enrollment</div>
                  <div>• Custom deployment support and dedicated account manager</div>
                </div>
                {subRecord?.plan?.toLowerCase() === "enterprise" ? (
                  <Button disabled size="small" style={{ width: "100%" }}>Current Active Plan</Button>
                ) : (
                  <Button 
                    appearance="outline" 
                    size="small"
                    style={{ width: "100%" }}
                    onClick={() => window.open("mailto:sales@marginpro.com?subject=MarginPro%20Enterprise%20Inquiry")}
                  >
                    Contact Enterprise Sales
                  </Button>
                )}
              </div>

            </div>
          </div>
        )}

        {/* ────────────────────────────────────────────────────────────────────── */}
        {/* TAB 2: SINGLE TEMPLATES (METERED DIMENSION)                            */}
        {/* ────────────────────────────────────────────────────────────────────── */}
        {activeTab === "single" && (
          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            {/* Industry Filter Dropdown */}
            <div style={{ marginBottom: "6px" }}>
              <Caption1 style={{ color: tokens.colorNeutralForeground3, display: "block", marginBottom: "4px", fontWeight: 600 }}>
                FILTER BLUEPRINTS
              </Caption1>
              <Dropdown
                style={{ width: "100%", minWidth: "100%" }}
                value={selectedIndustry === "All" ? `All Blueprints (${countFor("All")})` : `${selectedIndustry} (${countFor(selectedIndustry)})`}
                selectedOptions={[selectedIndustry]}
                onOptionSelect={(_, data) => setSelectedIndustry(data.optionValue as IndustryOption)}
              >
                {["All", "Strategic Planning", "Project Management", "Academic & Research", "Professional Productivity"].map((opt) => (
                  <Option key={opt} value={opt}>
                    {opt === "All" ? `All Blueprints (${countFor("All")})` : `${opt} (${countFor(opt)})`}
                  </Option>
                ))}
              </Dropdown>
            </div>

            {/* Template Cards Grouped */}
            {Object.entries(grouped).map(([sectionKey, templates]) => {
              const [industry, section] = sectionKey.split(" › ");
              return (
                <div key={sectionKey}>
                  <div className={styles.sectionHeader}>
                    {industry} › {section}
                  </div>
                  {templates.map((t) => {
                    const isUnlocked = unlockedState[t.id];
                    return (
                      <div className={styles.card} key={t.id}>
                        <div className={styles.cardHeader}>
                          <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            {t.icon}
                            <span className={styles.cardTitle}>{t.title}</span>
                          </span>
                          <span className={styles.priceBadge} style={{
                            borderColor: isUnlocked ? "#2f8570" : "#ccc",
                            color: isUnlocked ? "#2f8570" : "#333",
                            backgroundColor: isUnlocked ? "#f0faf7" : "#fff"
                          }}>
                            {isUnlocked ? "Unlocked" : `$${(t.price / 100).toFixed(2)}`}
                          </span>
                        </div>
                        <Body1 style={{ color: tokens.colorNeutralForeground3, fontSize: "12px", lineHeight: "1.4" }}>
                          {t.description}
                        </Body1>
                        {subRecord?.plan === "Professional" && subRecord?.status === "trial" && (
                          <div style={{
                            marginTop: "8px",
                            padding: "8px 10px",
                            borderRadius: "6px",
                            backgroundColor: "rgba(123, 191, 174, 0.1)",
                            border: "1px dashed rgba(123, 191, 174, 0.4)",
                            fontSize: "11px",
                            color: tokens.colorNeutralForeground2,
                            fontWeight: 500,
                            lineHeight: "1.4"
                          }}>
                            💡 <strong>Trial Access:</strong> This template is unlocked during your 7-day Professional Trial. To continue using it after the trial, you will need to either purchase the template individually or subscribe to the Professional tier.
                          </div>
                        )}
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "8px" }}>
                          <Button 
                            size="small" 
                            appearance="subtle" 
                            icon={<Eye24Regular />} 
                            onClick={() => setPreviewTemplate(t)}
                          >
                            Preview
                          </Button>
                          {isUnlocked ? (
                            <Button
                              size="small"
                              appearance="primary"
                              className={styles.btnPrimary}
                              onClick={() => onUseTemplate(t.id, t.title, t.editorContent, t.htmlContent, t.usesTable)}
                            >
                              Use Template
                            </Button>
                          ) : (
                            <Button
                              size="small"
                              appearance="primary"
                              className={styles.btnPrimary}
                              onClick={() => setBuyingTemplate(t)}
                            >
                              Buy ${(t.price / 100).toFixed(2)}
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}

        {/* ────────────────────────────────────────────────────────────────────── */}
        {/* TAB 3: CATEGORY PACKS (LIFETIME BUYS)                                  */}
        {/* ────────────────────────────────────────────────────────────────────── */}
        {activeTab === "packs" && (
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <div style={{ padding: "10px", backgroundColor: "#fff9e6", borderLeft: "4px solid #ffc107", borderRadius: "4px" }}>
              <Caption1 style={{ color: "#664d03", fontWeight: 600 }}>
                💡 <b>Lifetime Category Packs:</b> Pay a one-time $24.99 fee to permanently unlock all current and future blueprints in that specific category!
              </Caption1>
            </div>

            {CATEGORY_PACKS.map((pack) => {
              return (
                <div className={styles.card} key={pack.id}>
                  <div className={styles.cardHeader}>
                    <span className={styles.cardTitle}>{pack.title}</span>
                    <span className={styles.priceBadge} style={{ borderColor: "#2f8570", color: "#2f8570" }}>
                      Lifetime Buy
                    </span>
                  </div>
                  <Body1 style={{ color: tokens.colorNeutralForeground3, fontSize: "12px", lineHeight: "1.4" }}>
                    {pack.description}
                  </Body1>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "6px" }}>
                    <Caption1 style={{ color: tokens.colorNeutralForeground3, fontWeight: 600 }}>
                      🎁 Includes {pack.templatesCount} advanced templates
                    </Caption1>
                    <Button
                      size="small"
                      appearance="primary"
                      className={styles.btnPrimary}
                      onClick={() => setBuyingPack(pack)}
                    >
                      Unlock Pack — $24.99
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Dialog: Confirm Single Template Purchase ── */}
      {buyingTemplate && (
        <Dialog open={!!buyingTemplate} onOpenChange={() => setBuyingTemplate(null)}>
          <DialogSurface>
            <DialogTitle>Confirm Template Unlock</DialogTitle>
            <DialogBody>
              <DialogContent>
                <Body1 block>
                  Unlock <strong>{buyingTemplate.title}</strong> permanently for <strong>$4.99</strong> via secure Stripe checkout.
                </Body1>
                <Body1 block style={{ marginTop: "8px", color: tokens.colorNeutralForeground3 }}>
                  One-time purchase — all sales are final and non-refundable.
                </Body1>
              </DialogContent>
              <DialogActions>
                <Button appearance="subtle" disabled={stripeBusy} onClick={() => setBuyingTemplate(null)}>Cancel</Button>
                <Button
                  appearance="primary"
                  style={{ backgroundColor: "#59766C" }}
                  disabled={stripeBusy}
                  icon={stripeBusy ? <Spinner size="tiny" /> : undefined}
                  onClick={() => handlePurchaseSingle(buyingTemplate)}
                >
                  {stripeBusy ? "Opening checkout…" : "Continue to Payment"}
                </Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      )}

      {/* ── Dialog: Confirm Category Pack Purchase ── */}
      {buyingPack && (
        <Dialog open={!!buyingPack} onOpenChange={() => setBuyingPack(null)}>
          <DialogSurface>
            <DialogTitle>Confirm Lifetime Category Unlock</DialogTitle>
            <DialogBody>
              <DialogContent>
                <Body1 block style={{ marginBottom: "8px" }}>
                  Unlock the <strong>{buyingPack.title}</strong> for <strong>$24.99</strong>?
                </Body1>
                <Body1 block style={{ color: tokens.colorNeutralForeground3 }}>
                  This unlocks lifetime access to all current and future templates under the <strong>{buyingPack.category}</strong> suite permanently. One-time payment via secure Stripe checkout.
                </Body1>
                <Body1 block style={{ marginTop: "8px", color: tokens.colorNeutralForeground3 }}>
                  All sales are final and non-refundable.
                </Body1>
              </DialogContent>
              <DialogActions>
                <Button appearance="subtle" disabled={stripeBusy} onClick={() => setBuyingPack(null)}>Cancel</Button>
                <Button
                  appearance="primary"
                  style={{ backgroundColor: "#59766C" }}
                  disabled={stripeBusy}
                  icon={stripeBusy ? <Spinner size="tiny" /> : undefined}
                  onClick={() => handlePurchasePack(buyingPack)}
                >
                  {stripeBusy ? "Opening checkout…" : "Continue to Payment"}
                </Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      )}

      {/* ── Dialog: Preview ── */}
      {previewTemplate && (
        <Dialog open={!!previewTemplate} onOpenChange={() => setPreviewTemplate(null)}>
          <DialogSurface style={{ width: "96%", maxWidth: "96%", padding: 0, overflow: "hidden" }}>
            <DialogTitle
              style={{ padding: "14px 16px 10px", borderBottom: "1px solid #e8e8e8", fontSize: "14px", fontWeight: 700 }}
              action={
                <Button appearance="subtle" size="small" onClick={() => setPreviewTemplate(null)}
                  style={{ minWidth: 0, padding: "4px 8px", fontSize: "16px", lineHeight: 1 }}
                >
                  ✕
                </Button>
              }
            >
              <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <Eye24Regular style={{ width: 18, height: 18, color: "#59766C" }} />
                {previewTemplate.title}
              </span>
            </DialogTitle>
            <DialogBody style={{ padding: 0 }}>
              <DialogContent style={{ padding: 0 }}>
                <div style={{
                  background: "linear-gradient(90deg,#f0faf7,#e8f4f0)",
                  borderBottom: "1px solid #c8e6de",
                  padding: "5px 14px",
                  display: "flex", alignItems: "center", gap: "6px",
                }}>
                  <Eye24Regular style={{ width: 12, height: 12, color: "#59766C" }} />
                  <Caption1 style={{ color: "#59766C", fontSize: "9px", fontWeight: 600 }}>
                    READ-ONLY PREVIEW — Unlock to use inside your document
                  </Caption1>
                </div>
                <iframe
                  srcDoc={`
                    <!DOCTYPE html>
                    <html>
                    <head>
                    <meta charset="utf-8">
                    <style>
                      html, body { margin: 0; padding: 14px; font-family: Calibri, sans-serif; font-size: 10pt; color: #333; background: #fff; }
                      table { border-collapse: collapse; width: 100%; }
                      th { background-color: #f3f2f1; color: #222; padding: 10px 14px; border: 0.5pt solid #d1d1d1; text-align: left; font-weight: 700; }
                      td { padding: 10px 14px; border: 0.5pt solid #d1d1d1; vertical-align: top; color: #444; }
                    </style>
                    </head>
                    <body>${previewTemplate.htmlContent}</body>
                    </html>
                  `}
                  sandbox="allow-same-origin"
                  title={`Preview: ${previewTemplate.title}`}
                  style={{ width: "100%", height: "360px", border: "none", display: "block" }}
                />
              </DialogContent>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      )}
    </div>
  );
};

export default Marketplace;
